import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Heart, 
    Users, 
    User, 
    ChevronRight, 
    Sparkles,
    Brain,
    RefreshCw,
    Shield,
    Wind,
    MessageCircle,
    Compass,
    HeartHandshake,
    Check,
    LogIn,
    Waves,
    Battery,
    Zap,
    Unplug,
    AlertCircle,
    HelpCircle
} from 'lucide-react';
import { PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { EmotionReferenceGuide } from '../components/cycle/EmotionReferenceGuide';
import { trackGlobalToolUsage } from './UnifiedHome';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * InitialAssessment - Onboarding Questionnaire
 * 
 * A comprehensive assessment accessible to BOTH registered and unregistered users.
 * Content is dynamically loaded from the API and can be edited via admin Tool Editor.
 * 
 * Flow:
 * 1. Welcome
 * 2. What brings you here? (primary need)
 * 3. Current challenges
 * 4. Relationship challenges (conditional)
 * 5. Emotional check-in (EmotionReferenceGuide)
 * 6. Body state
 * 7. Personalized recommendations
 */

const ASSESSMENT_COMPLETE_KEY = 'coregulate_initial_assessment_complete';
const ASSESSMENT_DATA_KEY = 'coregulate_assessment_data';

// Icon mapping for dynamic icons from API
const ICON_MAP = {
    Heart,
    Users,
    User,
    Sparkles,
    Brain,
    RefreshCw,
    Shield,
    Wind,
    MessageCircle,
    Compass,
    HeartHandshake,
    Waves,
    Battery,
    Zap,
    Unplug,
    AlertCircle
};

// Default fallback config (used if API fails)
const DEFAULT_CONFIG = {
    welcome: {
        title: 'Welcome to CoRegulate',
        subtitle: 'Made for ADHD hearts, shaped by ADHD minds.',
        message: "You're already doing something brave by being here. Whether you're feeling overwhelmed, disconnected, or just curious — this is a safe space to explore at your own pace.",
        start_button: "Let's Begin",
        skip_text: 'Skip for now'
    },
    primary_need: {
        title: 'What brings you here?',
        subtitle: 'There\'s no wrong answer',
        options: [
            { id: 'regulate-self', label: 'I need help regulating myself', description: 'Grounding, calming, self-soothing', icon: 'Wind', variant: 'warm' },
            { id: 'relationship-help', label: 'I need help with my relationship', description: 'Connection, communication, repair', icon: 'HeartHandshake', variant: 'warm' },
            { id: 'both', label: 'Both', description: '', icon: 'Heart', variant: 'neutral' },
            { id: 'exploring', label: 'Just exploring', description: '', icon: 'Compass', variant: 'neutral' }
        ]
    },
    support_focus: {
        title: 'What kind of support are you looking for?',
        subtitle: 'This helps us personalize your experience',
        options: [
            { id: 'individual', label: 'Individual ADHD support', description: 'Tools and guidance for managing my own ADHD', icon: 'User', variant: 'warm' },
            { id: 'relationship', label: 'Relationship support', description: 'Help with how ADHD affects my relationships', icon: 'HeartHandshake', variant: 'warm' },
            { id: 'both', label: 'A bit of both', description: 'I need support in both areas', icon: 'Heart', variant: 'neutral' }
        ]
    },
    challenges: {
        title: 'What are you struggling with?',
        subtitle: 'Select all that apply',
        options: [
            { id: 'overwhelm', label: 'Feeling overwhelmed often', icon: 'Brain' },
            { id: 'emotional-regulation', label: 'Managing big emotions', icon: 'Wind' },
            { id: 'communication', label: 'Difficulty communicating', icon: 'MessageCircle' },
            { id: 'disconnection', label: 'Feeling disconnected', icon: 'Users' },
            { id: 'adhd-impact', label: 'ADHD affecting my relationships', icon: 'Brain' },
            { id: 'relationship-help', label: 'I need help with my relationship', icon: 'HeartHandshake' }
        ]
    },
    relationship_challenges: {
        title: 'Do any of these sound familiar?',
        subtitle: 'Select all that apply',
        options: [
            { id: 'shutdown', label: 'Shutting down during conflict', icon: 'Shield' },
            { id: 'same-arguments', label: 'Having the same arguments', icon: 'RefreshCw' },
            { id: 'unmet-needs', label: 'Needs not being met', icon: 'Heart' }
        ]
    },
    emotions: {
        title: 'What emotions feel present lately?',
        subtitle: 'Select up to 3',
        max_selections: 3
    },
    body_state: {
        title: 'How does your body feel right now?',
        subtitle: 'This helps us suggest the right tools',
        options: [
            { id: 'tense', label: 'Tense', description: 'Tight shoulders, jaw, or chest', variant: 'warm' },
            { id: 'overwhelmed', label: 'Overwhelmed', description: 'Too much, hard to focus', variant: 'warm' },
            { id: 'flat', label: 'Flat', description: 'Low energy, disconnected', variant: 'neutral' },
            { id: 'okay', label: 'Pretty okay', description: 'Relatively balanced right now', variant: 'neutral' },
            { id: 'unsure', label: 'Not sure', description: 'Hard to tell right now', variant: 'neutral' }
        ]
    },
    results: {
        title: 'Your personalized path',
        subtitle: "Based on what you shared, here's where to start."
    }
};

// Glass card style variants
const VARIANT_STYLES = {
    warm: 'glass-card bg-[#FFBB98]/30 border border-[#FFBB98]/40 hover:border-[#FFBB98]/60',
    neutral: 'glass-card bg-white/60 border border-white/40 hover:border-[#1D6B6B]/30',
    selected: 'glass-card bg-[#1D6B6B]/15 border-2 border-[#1D6B6B]/50'
};

const InitialAssessment = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    
    // Config state
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [configLoading, setConfigLoading] = useState(true);
    
    // Flow state
    const [step, setStep] = useState('welcome');
    const [fadeIn, setFadeIn] = useState(false);
    
    // Help prompt state - shows after 30s of inactivity
    const [showHelpPrompt, setShowHelpPrompt] = useState(false);
    const [helpPromptDismissed, setHelpPromptDismissed] = useState(false);
    const inactivityTimerRef = useRef(null);
    
    // Assessment answers
    const [primaryNeed, setPrimaryNeed] = useState(null);
    const [supportFocus, setSupportFocus] = useState(null);
    const [challenges, setChallenges] = useState([]);
    const [relationshipChallenges, setRelationshipChallenges] = useState([]);
    const [selectedEmotions, setSelectedEmotions] = useState([]);
    const [expandedEmotion, setExpandedEmotion] = useState(null);
    const [bodyState, setBodyState] = useState(null);

    // Fetch config from API
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${API_URL}/api/assessment/config`);
                if (response.ok) {
                    const data = await response.json();
                    // Merge with defaults to ensure all fields exist
                    setConfig(prev => ({
                        ...prev,
                        ...data,
                        welcome: { ...prev.welcome, ...data.welcome },
                        primary_need: { ...prev.primary_need, ...data.primary_need },
                        challenges: { ...prev.challenges, ...data.challenges },
                        relationship_challenges: { ...prev.relationship_challenges, ...data.relationship_challenges },
                        emotions: { ...prev.emotions, ...data.emotions },
                        body_state: { ...prev.body_state, ...data.body_state },
                        results: { ...prev.results, ...data.results }
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch assessment config:', error);
                // Keep using defaults
            } finally {
                setConfigLoading(false);
            }
        };
        fetchConfig();
    }, []);

    // Check if relationship-specific question should show
    const showRelationshipChallenges = challenges.includes('adhd-impact') || challenges.includes('relationship-help');

    // Dynamic steps based on selections
    const getSteps = () => {
        const baseSteps = ['welcome', 'primary-need', 'support-focus', 'challenges'];
        if (showRelationshipChallenges) {
            baseSteps.push('relationship-challenges');
        }
        baseSteps.push('emotions', 'body', 'results');
        return baseSteps;
    };
    
    const steps = getSteps();
    const currentStepIndex = steps.indexOf(step);
    const totalSteps = steps.filter(s => s !== 'welcome' && s !== 'results').length;
    const currentStepNumber = steps.slice(1, currentStepIndex + 1).filter(s => s !== 'results').length;
    const progress = currentStepIndex > 0 && currentStepIndex < steps.length - 1 
        ? (currentStepNumber / totalSteps) * 100 
        : currentStepIndex === steps.length - 1 ? 100 : 0;

    useEffect(() => {
        setTimeout(() => setFadeIn(true), 100);
    }, []);

    useEffect(() => {
        setFadeIn(false);
        const timer = setTimeout(() => setFadeIn(true), 50);
        return () => clearTimeout(timer);
    }, [step]);

    // Inactivity timer for help prompt
    useEffect(() => {
        // Only show help prompt on question steps (not welcome or results)
        const questionSteps = ['primary-need', 'support-focus', 'challenges', 'relationship-challenges', 'emotions', 'body'];
        const isQuestionStep = questionSteps.includes(step);
        
        // Reset help prompt state when step changes
        setShowHelpPrompt(false);
        setHelpPromptDismissed(false);
        
        if (!isQuestionStep) return;
        
        // Clear any existing timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        
        // Start 30 second timer
        inactivityTimerRef.current = setTimeout(() => {
            if (!helpPromptDismissed) {
                setShowHelpPrompt(true);
            }
        }, 30000); // 30 seconds
        
        // Cleanup on unmount or step change
        return () => {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [step, helpPromptDismissed]);

    // Reset inactivity timer on user interaction
    const resetInactivityTimer = () => {
        setShowHelpPrompt(false);
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        // Restart timer only if not dismissed
        if (!helpPromptDismissed) {
            const questionSteps = ['primary-need', 'support-focus', 'challenges', 'relationship-challenges', 'emotions', 'body'];
            if (questionSteps.includes(step)) {
                inactivityTimerRef.current = setTimeout(() => {
                    setShowHelpPrompt(true);
                }, 30000);
            }
        }
    };

    // Get icon component from string name
    const getIcon = (iconName) => {
        return ICON_MAP[iconName] || Sparkles;
    };

    // Get style class for option based on variant
    const getOptionStyle = (option, isSelected = false) => {
        if (isSelected) return VARIANT_STYLES.selected;
        const variant = option.variant || 'warm';
        return VARIANT_STYLES[variant] || VARIANT_STYLES.warm;
    };

    // Get personalized recommendations
    const getRecommendations = () => {
        const recs = [];
        const insights = [];
        
        // Combine all challenges for recommendation logic
        const allChallenges = [...challenges, ...relationshipChallenges];

        // Based on primary need
        if (primaryNeed === 'regulate-self' || primaryNeed === 'both') {
            recs.push({
                id: 'regulation',
                title: 'Regulation Tools',
                description: 'Start here for grounding and calming exercises.',
                path: '/regulate',
                icon: Wind,
                variant: 'sage',
                priority: 1
            });
        }

        if (primaryNeed === 'relationship-help' || primaryNeed === 'both') {
            recs.push({
                id: 'relationship-support',
                title: 'Relationship Support',
                description: 'Tools for connection, patterns, and repair.',
                path: '/relationship-hub',
                icon: HeartHandshake,
                variant: 'rose',
                priority: 1
            });
        }

        // Based on challenges (using combined allChallenges)
        if (allChallenges.includes('same-arguments') || allChallenges.includes('disconnection') || allChallenges.includes('disconnected')) {
            recs.push({
                id: 'break-cycle',
                title: 'Break the Cycle',
                description: 'Understand and shift repeating patterns.',
                path: '/relationship-hub/break-the-cycle',
                icon: RefreshCw,
                variant: 'lavender',
                priority: 2
            });
            insights.push({
                title: 'Pattern Alert',
                description: 'Repeating arguments often signal an underlying cycle. Understanding the pattern is the first step to changing it.'
            });
        }

        if (allChallenges.includes('shutdown') || allChallenges.includes('overwhelm')) {
            recs.push({
                id: 'overwhelm-nav',
                title: 'Regulation Navigator',
                description: 'Gentle grounding when things feel like too much.',
                path: '/regulate/regulation-navigator',
                icon: Compass,
                variant: 'sage',
                priority: 1
            });
            insights.push({
                title: 'Shutdown Protection',
                description: 'Shutting down is your nervous system protecting you. Learning to regulate can help you stay present.'
            });
        }

        if (allChallenges.includes('unmet-needs') || allChallenges.includes('needs-unmet')) {
            recs.push({
                id: 'solo-need',
                title: 'What Do I Need?',
                description: 'Explore and name your needs clearly.',
                path: '/relationship-hub/solo-need',
                icon: Heart,
                variant: 'lavender',
                priority: 2
            });
        }

        if (allChallenges.includes('communication')) {
            recs.push({
                id: 'relationship-checkin',
                title: 'Relationship Check-in',
                description: 'Assess your relationship patterns.',
                path: '/relationship-hub/check-in',
                icon: MessageCircle,
                variant: 'blue',
                priority: 2
            });
        }

        if (allChallenges.includes('adhd-impact')) {
            recs.push({
                id: 'adhd-triggers',
                title: 'ADHD Relationship Triggers',
                description: 'How ADHD shows up in relationships.',
                path: '/regulate/adhd-triggers',
                icon: Brain,
                variant: 'lavender',
                priority: 2
            });
            insights.push({
                title: 'ADHD-Aware Support',
                description: 'This app is designed specifically for ADHD minds. Every tool considers how ADHD affects regulation and relationships.'
            });
        }

        // Based on body state - add immediate help if needed
        if (bodyState === 'overwhelmed' || bodyState === 'tense') {
            const regIdx = recs.findIndex(r => r.id === 'overwhelm-nav');
            if (regIdx === -1) {
                recs.unshift({
                    id: 'micro-reset',
                    title: 'Micro Reset',
                    description: 'A quick reset to help right now.',
                    path: '/regulate/micro-reset',
                    icon: Wind,
                    variant: 'sage',
                    priority: 0
                });
            }
        }

        // Based on selected emotions
        if (selectedEmotions.some(e => ['scared', 'overwhelmed', 'ashamed'].includes(e))) {
            if (!recs.find(r => r.id === 'overwhelm-nav')) {
                recs.push({
                    id: 'overwhelm-nav',
                    title: 'Regulation Navigator',
                    description: 'Gentle grounding for big emotions.',
                    path: '/regulate/regulation-navigator',
                    icon: Compass,
                    variant: 'sage',
                    priority: 1
                });
            }
        }

        if (selectedEmotions.some(e => ['sad', 'numb', 'lonely'].includes(e))) {
            insights.push({
                title: 'Connection Matters',
                description: "Feeling disconnected is painful. The tools here can help you reconnect - with yourself first, then with others."
            });
        }

        // If exploring, give general path
        if (primaryNeed === 'exploring' && recs.length === 0) {
            recs.push({
                id: 'home',
                title: 'Explore the Dashboard',
                description: 'See all available tools and features.',
                path: '/home',
                icon: Sparkles,
                variant: 'lavender',
                priority: 1
            });
        }

        // Sort by priority and dedupe
        recs.sort((a, b) => a.priority - b.priority);
        const seen = new Set();
        const uniqueRecs = recs.filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
        });

        return { recommendations: uniqueRecs.slice(0, 4), insights: insights.slice(0, 2) };
    };

    // Handlers
    const handleNext = () => {
        const currentSteps = getSteps();
        const currentIndex = currentSteps.indexOf(step);
        if (currentIndex < currentSteps.length - 1) {
            setStep(currentSteps[currentIndex + 1]);
        }
    };

    const handleBack = () => {
        const currentSteps = getSteps();
        const currentIndex = currentSteps.indexOf(step);
        if (currentIndex > 0) {
            setStep(currentSteps[currentIndex - 1]);
        }
    };

    const handlePrimaryNeedSelect = (id) => {
        resetInactivityTimer();
        setPrimaryNeed(id);
        setTimeout(() => setStep('support-focus'), 300);
    };

    const handleSupportFocusSelect = (id) => {
        resetInactivityTimer();
        setSupportFocus(id);
        setTimeout(() => setStep('challenges'), 300);
    };

    const handleChallengeToggle = (id) => {
        resetInactivityTimer();
        setChallenges(prev => {
            if (prev.includes(id)) {
                return prev.filter(c => c !== id);
            }
            return [...prev, id];
        });
    };

    const handleRelationshipChallengeToggle = (id) => {
        resetInactivityTimer();
        setRelationshipChallenges(prev => {
            if (prev.includes(id)) {
                return prev.filter(c => c !== id);
            }
            return [...prev, id];
        });
    };

    const handleChallengesContinue = () => {
        const needsRelationshipStep = challenges.includes('adhd-impact') || challenges.includes('relationship-help');
        if (needsRelationshipStep) {
            setStep('relationship-challenges');
        } else {
            setStep('emotions');
        }
    };

    const handleEmotionSelect = (emotionId) => {
        resetInactivityTimer();
        setSelectedEmotions(prev => {
            if (prev.includes(emotionId)) {
                return prev.filter(id => id !== emotionId);
            }
            if (prev.length >= (config.emotions?.max_selections || 3)) return prev;
            return [...prev, emotionId];
        });
    };

    const handleBodyStateSelect = (id) => {
        resetInactivityTimer();
        setBodyState(id);
        setTimeout(() => setStep('results'), 300);
    };

    const handleComplete = async (path) => {
        const assessmentData = {
            primaryNeed,
            supportFocus,
            challenges,
            relationshipChallenges,
            selectedEmotions,
            bodyState,
            completedAt: new Date().toISOString()
        };
        localStorage.setItem(ASSESSMENT_DATA_KEY, JSON.stringify(assessmentData));
        localStorage.setItem(ASSESSMENT_COMPLETE_KEY, 'true');
        
        if (isAuthenticated) {
            localStorage.setItem('coregulate_hasCompletedOnboarding', 'true');
            
            // Save assessment data to backend for personalization
            try {
                await fetch(`${API_URL}/api/assessment/save`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                    },
                    body: JSON.stringify({
                        assessment_data: {
                            primary_need: primaryNeed,
                            support_focus: supportFocus,
                            challenges: challenges,
                            relationship_challenges: relationshipChallenges,
                            selected_emotions: selectedEmotions,
                            body_state: bodyState
                        }
                    })
                });
            } catch (error) {
                console.error('Failed to save assessment:', error);
                // Continue anyway - local storage is fallback
            }
            
            trackGlobalToolUsage(path);
            navigate(path);
        } else {
            navigate('/auth', { state: { from: 'assessment', returnTo: path } });
        }
    };

    const handleSkip = () => {
        localStorage.setItem(ASSESSMENT_COMPLETE_KEY, 'true');
        
        if (isAuthenticated) {
            localStorage.setItem('coregulate_hasCompletedOnboarding', 'true');
            navigate('/home');
        } else {
            navigate('/auth', { state: { from: 'assessment' } });
        }
    };

    const handleSignUp = async () => {
        const assessmentData = {
            primaryNeed,
            supportFocus,
            challenges,
            relationshipChallenges,
            selectedEmotions,
            bodyState,
            completedAt: new Date().toISOString()
        };
        localStorage.setItem(ASSESSMENT_DATA_KEY, JSON.stringify(assessmentData));
        localStorage.setItem(ASSESSMENT_COMPLETE_KEY, 'true');
        
        // If already authenticated, save to backend
        if (isAuthenticated) {
            try {
                await fetch(`${API_URL}/api/assessment/save`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                    },
                    body: JSON.stringify({
                        assessment_data: {
                            primary_need: primaryNeed,
                            support_focus: supportFocus,
                            challenges: challenges,
                            relationship_challenges: relationshipChallenges,
                            selected_emotions: selectedEmotions,
                            body_state: bodyState
                        }
                    })
                });
            } catch (error) {
                console.error('Failed to save assessment:', error);
            }
        }
        
        navigate('/auth', { state: { from: 'assessment' } });
    };

    const results = step === 'results' ? getRecommendations() : null;

    const variantColors = {
        sage: 'bg-[#FFBB98]/40',
        rose: 'bg-[#FFBB98]/40',
        lavender: 'bg-[#7D8E95]/40',
        blue: 'bg-[#7D8E95]/40'
    };

    const variantIconColors = {
        sage: 'text-[#1D6B6B]',
        rose: 'text-[#FFBB98]',
        lavender: 'text-[#7D8E95]',
        blue: 'text-[#7D8E95]'
    };

    // Show loading state while fetching config
    if (configLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-[#1D6B6B] animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            {/* Inspirational quote - only on first assessment step */}
            {step === 'primary-need' && (
                <div className="bg-[#7D8E95]/15 border-b border-[#7D8E95]/30">
                    <div className="max-w-xl mx-auto px-6 py-4 text-center">
                        <BodyText className="text-[#7D8E95] leading-relaxed italic">
                            "Embrace your journey – detours often lead to the best destinations."
                        </BodyText>
                        <SmallText className="text-[#7D8E95] mt-1 block">
                            – Unknown
                        </SmallText>
                    </div>
                </div>
            )}
            
            {/* Progress header (not on welcome or results) */}
            {step !== 'welcome' && step !== 'results' && (
                <header className="sticky top-0 z-40 bg-[#FBF6EE]/90 backdrop-blur-md border-b border-[#F0E8DA]/60">
                    <div className="max-w-xl mx-auto px-6 py-4">
                        <div className="flex items-center justify-between mb-2">
                            <SmallText className="text-[#7D8E95] font-medium">Getting to know you</SmallText>
                            <SmallText className="text-[#7D8E95]">{currentStepIndex} of {steps.length - 2}</SmallText>
                        </div>
                        <div className="h-1.5 bg-[#FFBB98] rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-[#7D8E95] rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                </header>
            )}

            <main className="max-w-md mx-auto px-6 py-12 min-h-screen flex flex-col">
                
                {/* Subtle Help Prompt - appears after 30s of inactivity */}
                {showHelpPrompt && !helpPromptDismissed && (
                    <div 
                        className="fixed bottom-24 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-500"
                        data-testid="help-prompt"
                    >
                        <div className="bg-white rounded-2xl shadow-lg border border-white/40 p-4 max-w-[280px]">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#7D8E95]/20 flex items-center justify-center flex-shrink-0">
                                    <HelpCircle className="w-4 h-4 text-[#1D6B6B]" />
                                </div>
                                <div className="flex-1">
                                    <SmallText className="text-[#1D6B6B] font-medium block mb-1">
                                        Need help?
                                    </SmallText>
                                    <SmallText className="text-[#7D8E95] leading-relaxed block mb-3">
                                        No pressure. There's no wrong answer here. Just pick what feels closest.
                                    </SmallText>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setHelpPromptDismissed(true);
                                                setShowHelpPrompt(false);
                                            }}
                                            className="text-xs text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                                        >
                                            Got it
                                        </button>
                                        <span className="text-[#D0D0CD]">•</span>
                                        <button
                                            onClick={() => {
                                                setHelpPromptDismissed(true);
                                                setShowHelpPrompt(false);
                                                // Open AI companion if available
                                                const aiBtn = document.querySelector('[data-testid="ai-companion-btn"]');
                                                if (aiBtn) aiBtn.click();
                                            }}
                                            className="text-xs text-[#1D6B6B] hover:text-[#1D6B6B] transition-colors font-medium"
                                        >
                                            Talk to guide
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* WELCOME */}
                {step === 'welcome' && (
                    <div className={`flex-1 flex flex-col justify-center transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="flex justify-center mb-8">
                            <div className="w-24 h-24 rounded-full bg-[#7D8E95]/20 flex items-center justify-center">
                                <div className="w-16 h-16 rounded-full bg-[#7D8E95]/30 flex items-center justify-center">
                                    <Sparkles className="w-8 h-8 text-[#1D6B6B]" />
                                </div>
                            </div>
                        </div>
                        <div className="text-center mb-8">
                            <Heading level={1} className="text-[#1D6B6B] text-2xl md:text-3xl font-medium mb-4 leading-relaxed">
                                {config.welcome?.title || 'Welcome to CoRegulate'}
                            </Heading>
                            <BodyText className="text-[#7D8E95] text-lg leading-relaxed mb-4">
                                {config.welcome?.subtitle || 'Made for ADHD hearts, shaped by ADHD minds.'}
                            </BodyText>
                            
                            {/* Gentle welcoming message */}
                            <div className="bg-[#7D8E95]/10 rounded-2xl p-5 mb-4 border border-[#7D8E95]/20">
                                <BodyText className="text-[#5A6A57] leading-relaxed italic">
                                    "{config.welcome?.message || "You're already doing something brave by being here. Whether you're feeling overwhelmed, disconnected, or just curious — this is a safe space to explore at your own pace."}"
                                </BodyText>
                            </div>
                            
                            <BodyText className="text-[#7D8E95] leading-relaxed">
                                Let's take a moment to understand what you need, so we can guide you to the right tools.
                            </BodyText>
                        </div>
                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {step === 'welcome' && (
                    <PersistentBottomBar>
                        <PrimaryButton 
                            onClick={() => setStep('primary-need')} 
                            className="w-full" 
                            size="action" 
                            data-testid="start-assessment-btn"
                        >
                            {config.welcome?.start_button || "Let's Begin"}
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        <div className="text-center mt-1.5">
                            <button 
                                onClick={handleSkip}
                                className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                                data-testid="skip-assessment-btn"
                            >
                                {config.welcome?.skip_text || 'Skip for now'}
                            </button>
                        </div>
                    </PersistentBottomBar>
                )}

                {/* PRIMARY NEED */}
                {step === 'primary-need' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-8 pt-4">
                            <SmallText className="text-[#1D6B6B] font-medium mb-3">Your focus</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed">
                                {config.primary_need?.title || 'What brings you here?'}
                            </Heading>
                            {config.primary_need?.subtitle && (
                                <BodyText className="text-[#7D8E95] mt-2">{config.primary_need.subtitle}</BodyText>
                            )}
                        </div>
                        <div className="space-y-3 flex-1">
                            {(config.primary_need?.options || []).map((option) => {
                                const OptionIcon = getIcon(option.icon);
                                const styleClass = getOptionStyle(option);
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handlePrimaryNeedSelect(option.id)}
                                        className={`w-full p-5 rounded-2xl text-left 
                                                   transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]
                                                   ${styleClass}`}
                                        data-testid={`need-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-white/60 backdrop-blur-sm flex items-center justify-center border border-white/40">
                                                <OptionIcon className="w-5 h-5 text-[#1D6B6B]" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-[#1D6B6B] text-lg">{option.label}</div>
                                                {option.description && (
                                                    <div className="text-[#7D8E95] text-sm">{option.description}</div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-6 text-center">
                            <button onClick={handleBack} className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
                                Back
                            </button>
                        </div>
                    </div>
                )}

                {/* SUPPORT FOCUS */}
                {step === 'support-focus' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-8 pt-4">
                            <SmallText className="text-[#1D6B6B] font-medium mb-3">Your focus area</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed">
                                {config.support_focus?.title || 'What kind of support are you looking for?'}
                            </Heading>
                            {config.support_focus?.subtitle && (
                                <BodyText className="text-[#7D8E95] mt-2">{config.support_focus.subtitle}</BodyText>
                            )}
                        </div>
                        <div className="space-y-3 flex-1">
                            {(config.support_focus?.options || []).map((option) => {
                                const OptionIcon = getIcon(option.icon);
                                const styleClass = getOptionStyle(option);
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handleSupportFocusSelect(option.id)}
                                        className={`w-full p-5 rounded-2xl text-left 
                                                   transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]
                                                   ${styleClass}`}
                                        data-testid={`support-focus-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-white/60 backdrop-blur-sm flex items-center justify-center border border-white/40">
                                                <OptionIcon className="w-5 h-5 text-[#1D6B6B]" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-[#1D6B6B] text-lg">{option.label}</div>
                                                {option.description && (
                                                    <div className="text-[#7D8E95] text-sm">{option.description}</div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-6 text-center">
                            <button onClick={handleBack} className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
                                Back
                            </button>
                        </div>
                    </div>
                )}

                {/* CHALLENGES (Multi-select) */}
                {step === 'challenges' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-6 pt-4">
                            <SmallText className="text-[#1D6B6B] font-medium mb-3">Current challenges</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed mb-2">
                                {config.challenges?.title || 'What are you struggling with?'}
                            </Heading>
                            <BodyText className="text-[#7D8E95]">{config.challenges?.subtitle || 'Select all that apply'}</BodyText>
                        </div>
                        <div className="grid grid-cols-2 gap-3 flex-1">
                            {(config.challenges?.options || []).map((option) => {
                                const isSelected = challenges.includes(option.id);
                                const OptionIcon = getIcon(option.icon);
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handleChallengeToggle(option.id)}
                                        className={`p-4 rounded-2xl text-left transition-all duration-200 
                                                   hover:scale-[1.01] active:scale-[0.99]
                                                   ${isSelected 
                                                       ? 'glass-card bg-[#1D6B6B]/15 border-2 border-[#1D6B6B]/50' 
                                                       : 'glass-card bg-white/60 border border-white/40 hover:border-[#1D6B6B]/30'}`}
                                        data-testid={`challenge-${option.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                                                           ${isSelected ? 'bg-[#1D6B6B] border-[#1D6B6B]' : 'border-[#7D8E95]/50'}`}>
                                                {isSelected && <Check className="w-3 h-3 text-white" />}
                                            </div>
                                            <div>
                                                <OptionIcon className="w-4 h-4 text-[#1D6B6B] mb-1" />
                                                <span className="text-sm font-medium text-[#1D6B6B] leading-tight block">{option.label}</span>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'challenges' && (
                    <PersistentBottomBar>
                        <PrimaryButton 
                            onClick={handleChallengesContinue} 
                            className="w-full" 
                            size="action" 
                            data-testid="challenges-continue-btn"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        <div className="text-center mt-1.5">
                            <button onClick={handleBack} className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors">
                                Back
                            </button>
                        </div>
                    </PersistentBottomBar>
                )}

                {/* RELATIONSHIP CHALLENGES (conditional) */}
                {step === 'relationship-challenges' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-6 pt-4">
                            <SmallText className="text-[#1D6B6B] font-medium mb-3">In your relationship</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed mb-2">
                                {config.relationship_challenges?.title || 'Do any of these sound familiar?'}
                            </Heading>
                            <BodyText className="text-[#7D8E95]">{config.relationship_challenges?.subtitle || 'Select all that apply'}</BodyText>
                        </div>
                        <div className="space-y-3 flex-1">
                            {(config.relationship_challenges?.options || []).map((option) => {
                                const isSelected = relationshipChallenges.includes(option.id);
                                const OptionIcon = getIcon(option.icon);
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handleRelationshipChallengeToggle(option.id)}
                                        className={`w-full p-5 rounded-2xl text-left transition-all duration-200 
                                                   hover:scale-[1.01] active:scale-[0.99]
                                                   ${isSelected 
                                                       ? 'glass-card bg-[#1D6B6B]/15 border-2 border-[#1D6B6B]/50' 
                                                       : 'glass-card bg-white/60 border border-white/40 hover:border-[#1D6B6B]/30'}`}
                                        data-testid={`rel-challenge-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                                           ${isSelected ? 'bg-[#1D6B6B] border-[#1D6B6B]' : 'border-[#7D8E95]/50'}`}>
                                                {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <OptionIcon className="w-5 h-5 text-[#1D6B6B]" />
                                                <span className="font-medium text-[#1D6B6B]">{option.label}</span>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'relationship-challenges' && (
                    <PersistentBottomBar>
                        <PrimaryButton 
                            onClick={() => setStep('emotions')} 
                            className="w-full" 
                            size="action" 
                            data-testid="rel-challenges-continue-btn"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        <div className="text-center mt-1.5">
                            <button onClick={handleBack} className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors">
                                Back
                            </button>
                        </div>
                    </PersistentBottomBar>
                )}

                {/* EMOTIONS (EmotionReferenceGuide) */}
                {step === 'emotions' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-2 pt-2">
                            <SmallText className="text-[#1D6B6B] font-medium mb-1">Emotional check-in</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-lg md:text-xl font-medium leading-relaxed mb-1">
                                {config.emotions?.title || 'What emotions feel present lately?'}
                            </Heading>
                            <SmallText className="text-[#7D8E95]">
                                {config.emotions?.subtitle || `Select up to ${config.emotions?.max_selections || 3}`}
                            </SmallText>
                        </div>
                        <div className="flex-1 flex items-center justify-center">
                            <EmotionReferenceGuide
                                selectedEmotions={selectedEmotions}
                                onEmotionSelect={handleEmotionSelect}
                                expandedEmotion={expandedEmotion}
                                onExpandEmotion={setExpandedEmotion}
                            />
                        </div>
                        <BottomBarSpacer height="inline" />
                    </div>
                )}

                {step === 'emotions' && (
                    <PersistentBottomBar padding="inline" layout="inline">
                        <button 
                            onClick={handleBack} 
                            className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors px-4 py-2"
                        >
                            Back
                        </button>
                        <PrimaryButton 
                            onClick={() => setStep('body')} 
                            size="md" 
                            data-testid="emotions-continue-btn"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* BODY STATE */}
                {step === 'body' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-8 pt-4">
                            <SmallText className="text-[#1D6B6B] font-medium mb-3">Body check-in</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed">
                                {config.body_state?.title || 'How does your body feel right now?'}
                            </Heading>
                            {config.body_state?.subtitle && (
                                <BodyText className="text-[#7D8E95] mt-2">{config.body_state.subtitle}</BodyText>
                            )}
                        </div>
                        <div className="space-y-3 flex-1">
                            {(config.body_state?.options || []).map((option) => {
                                const styleClass = getOptionStyle(option);
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handleBodyStateSelect(option.id)}
                                        className={`w-full p-5 rounded-2xl text-left 
                                                   transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]
                                                   ${styleClass}`}
                                        data-testid={`body-${option.id}`}
                                    >
                                        <div className="font-medium text-[#1D6B6B] text-lg mb-1">{option.label}</div>
                                        {option.description && (
                                            <div className="text-[#7D8E95] text-sm">{option.description}</div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-6 text-center">
                            <button onClick={handleBack} className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
                                Back
                            </button>
                        </div>
                    </div>
                )}

                {/* RESULTS */}
                {step === 'results' && results && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-6 pt-4">
                            <div className="w-14 h-14 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-6 h-6 text-[#1D6B6B]" />
                            </div>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed mb-2">
                                {config.results?.title || 'Your personalized path'}
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                {config.results?.subtitle || "Based on what you shared, here's where to start."}
                            </BodyText>
                        </div>

                        {/* Insights */}
                        {results.insights.length > 0 && (
                            <div className="mb-6 space-y-3">
                                {results.insights.map((insight, index) => (
                                    <div className="glass-card rounded-2xl p-5"
                                        key={index}
                                        variant="elevated"
                                        padding="md"
                                        className="bg-[#7D8E95]/20 border border-[#7D8E95]/50"
                                    >
                                        <SmallText className="font-semibold text-[#7D8E95] mb-1 block">{insight.title}</SmallText>
                                        <SmallText className="text-[#7D8E95] leading-relaxed">{insight.description}</SmallText>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Recommendations */}
                        <div className="mb-8">
                            <SmallText className="text-[#7D8E95] font-medium mb-4 block">
                                Recommended for you
                            </SmallText>
                            <div className="space-y-3">
                                {results.recommendations.map((rec, index) => {
                                    const RecIcon = rec.icon;
                                    return (
                                        <button
                                            key={rec.id}
                                            onClick={() => handleComplete(rec.path)}
                                            className="w-full p-4 rounded-2xl bg-white border border-white/40 text-left
                                                     hover:border-[#7D8E95] hover:shadow-sm transition-all flex items-center gap-4
                                                     animate-fade-in"
                                            style={{ animationDelay: `${index * 100}ms` }}
                                            data-testid={`recommendation-${rec.id}`}
                                        >
                                            <div className={`w-12 h-12 rounded-full ${variantColors[rec.variant]} flex items-center justify-center`}>
                                                <RecIcon className={`w-5 h-5 ${variantIconColors[rec.variant]}`} />
                                            </div>
                                            <div className="flex-1">
                                                <BodyText className="font-medium text-[#1D6B6B]">{rec.title}</BodyText>
                                                <SmallText className="text-[#7D8E95]">{rec.description}</SmallText>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-[#7D8E95]" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Sign up prompt for unauthenticated users */}
                        {!isAuthenticated && (
                            <div className="glass-card rounded-2xl p-5"
                                variant="elevated" 
                                padding="lg" 
                                className="mb-6 bg-[#7D8E95]/10 border border-[#7D8E95]/30 text-center"
                            >
                                <div className="w-12 h-12 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-4">
                                    <LogIn className="w-5 h-5 text-[#1D6B6B]" />
                                </div>
                                <Heading level={3} className="text-[#1D6B6B] text-lg font-medium mb-2">
                                    Ready to get started?
                                </Heading>
                                <BodyText className="text-[#7D8E95] mb-4">
                                    Create a free account to access your personalized tools and track your progress.
                                </BodyText>
                                <PrimaryButton 
                                    onClick={handleSignUp}
                                    className="w-full"
                                    size="action"
                                    data-testid="signup-btn"
                                >
                                    Create Free Account
                                    <ChevronRight className="w-4 h-4 ml-1.5" />
                                </PrimaryButton>
                                <div className="mt-3">
                                    <button 
                                        onClick={() => navigate('/auth')}
                                        className="text-sm text-[#1D6B6B] hover:text-[#1D6B6B] transition-colors"
                                    >
                                        Already have an account? Sign in
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Dashboard link for authenticated users */}
                        {isAuthenticated && (
                            <div className="mt-auto text-center pb-4">
                                <button 
                                    onClick={() => handleComplete('/home')}
                                    className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                                    data-testid="go-to-dashboard-btn"
                                >
                                    Or explore the dashboard
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default InitialAssessment;
